/** One row's predecessor or successor cell: editable DepChips for in-scope
 *  ties, read-only context chips (✓ finished / ↗ other section) for the rest,
 *  severed-tie ghost chips, and the "+ add" typeahead. */
import React from "react";
import Tooltip from "../../../../../components/Tooltip/Tooltip";
import {
  predTypeFromShort,
  shortFromPredType,
  type DepGraphActivity,
  type DepGraphPredType,
} from "../../../../../models";
import { formatLagSuffix, type PredType } from "../../../../../utils/p6";
import { autoFontColor } from "../../../../../utils/color";
import DepChip from "../../DepChip/DepChip";
import DepChipTypeahead, {
  type DepCandidate,
  type ExternalCandidate,
} from "../../DepChipTypeahead/DepChipTypeahead";
import {
  activityLabel,
  driverDescriptor,
  zoneShort,
  type ResolvedPred,
  type ResolvedSucc,
} from "../../graphRows";
import type { DepEditIntents } from "../intents";

interface ChipCellProps {
  /** Loop ties as "predId>succId" — these chips flag red. */
  cycleEdges?: Set<string>;
  row: DepGraphActivity;
  side: "pred" | "succ";
  preds: ResolvedPred[];
  succs: ResolvedSucc[];
  readOnly: boolean;
  /** Compact = the table pills: code + relationship only (no name / zone). The
   *  Relationships panel renders full names with compact off. */
  compact?: boolean;
  /** "pill" (default) = table chips. "row" = the Relationships-panel chips
   *  (color dot + name + relationship on a subtle ground). */
  variant?: "pill" | "row";
  /** Anchors mode: outside ties are editable (retype / remove) too. */
  allowExternalEdits?: boolean;
  /** The add control's text (default "+ add") — anchors rows name the tie's
   *  direction ("+ Incoming successor" / "+ Outgoing pred"). */
  addLabel?: string;
  addTitle?: string;
  /** Severed-tie GHOST chips: names of in-section partners deleted by this
   *  draft, struck through where the replacement tie gets added. */
  ghosts?: string[];
  zonesByCode: Map<string, Set<string>>;
  allCandidates: DepCandidate[];
  /** Query-driven out-of-section search for the "+ add" popover. */
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
  intents: DepEditIntents;
}

const ChipCell: React.FC<ChipCellProps> = ({
  cycleEdges,
  row,
  side,
  preds,
  succs,
  readOnly,
  compact = false,
  variant = "pill",
  allowExternalEdits = false,
  addLabel = "+ add",
  addTitle,
  ghosts,
  zonesByCode,
  allCandidates,
  fetchExternals,
  intents,
}) => {
  const [adding, setAdding] = React.useState(false);
  const addBtnRef = React.useRef<HTMLButtonElement>(null);

  const candidates = React.useMemo(
    () => allCandidates.filter((c) => c.instanceId !== row.id),
    [allCandidates, row.id],
  );

  const handleClose = React.useCallback(() => setAdding(false), []);

  const onPick = (c: DepCandidate) => {
    if (side === "pred") intents.onAddEdge(c.instanceId, row.id);
    else intents.onAddEdge(row.id, c.instanceId);
    setAdding(false);
  };

  // Picking an out-of-section activity stages the same add-edge — its id is the
  // pred (this row's predecessor) or succ (this row feeds it). The graph rebuild
  // surfaces it as a driver on this row.
  const onPickExternal = (c: ExternalCandidate) => {
    const extId = String(c.externalActivityId);
    if (side === "pred") intents.onAddEdge(extId, row.id);
    else intents.onAddEdge(row.id, extId);
    setAdding(false);
  };

  // pred cell -> the resolved predecessors; succ cell -> resolved successors.
  const items: Array<{
    key: string;
    other: DepGraphActivity;
    succId: string;
    predId: string;
    type: DepGraphPredType;
    lagDays: number;
    editable: boolean;
    driving: boolean;
  }> = [];
  if (side === "pred") {
    for (const p of preds) {
      items.push({
        key:
          p.edge.edge_id != null
            ? `e-${p.edge.edge_id}`
            : `n-${p.pred.id}-${p.edge.type}-${p.edge.lag_hours}`,
        other: p.pred,
        succId: row.id,
        predId: p.pred.id,
        type: p.edge.type,
        lagDays: p.edge.lag_hours / 8,
        editable: !readOnly && (p.pred.in_scope || allowExternalEdits),
        driving: p.edge.driving,
      });
    }
  } else {
    for (const s of succs) {
      items.push({
        key:
          s.edge.edge_id != null
            ? `e-${s.edge.edge_id}`
            : `n-${s.succId}-${s.edge.type}-${s.edge.lag_hours}`,
        other: s.succ,
        succId: s.succId,
        predId: row.id,
        type: s.edge.type,
        lagDays: s.edge.lag_hours / 8,
        editable: !readOnly && (s.succ.in_scope || allowExternalEdits),
        driving: s.edge.driving,
      });
    }
  }

  return (
    <div className="dep-table-chip-cell">
      {items.map((it) => {
        const zonesForCode = zonesByCode.get(it.other.work_code);
        const showZone = (zonesForCode?.size ?? 0) > 1 ? zoneShort(it.other) : null;
        if (!it.editable) {
          const arrow = side === "pred" ? "←" : "→";
          const { done: isDone, origin } = driverDescriptor(it.other);
          const isExternal = !it.other.in_scope && !isDone;
          // A driver chip names what gates / is gated by this activity. The badge
          // says which kind (✓ finished work / ↗ another section); the tooltip
          // carries its home + driving dates so the relationship reads fully
          // without the driver needing its own canvas row.
          const kind = isDone
            ? `✓ ${origin}`
            : isExternal
              ? `↗ ${origin}`
              : "in section";
          const dates = `${it.other.start ?? "—"} → ${it.other.finish ?? "—"}`;
          const rel = `${it.type}${formatLagSuffix(it.lagDays * 8)}`;
          return (
            <Tooltip
              key={it.key}
              content={`${side === "pred" ? "Predecessor" : "Successor"} · ${rel} · ${kind}\n${it.other.task_code} · ${it.other.work_code} ${it.other.name}\n${dates}`}
            >
              <span
                className={"dep-table-context-chip" + (variant === "row" ? " dep-table-context-chip--row" : "") + (isDone ? " dep-table-context-chip--done" : isExternal ? " dep-table-context-chip--external" : "") + (it.driving ? " dep-table-context-chip--driving" : "")}
                style={
                  variant === "row" && it.other.color
                    ? { background: it.other.color, color: autoFontColor(it.other.color) }
                    : undefined
                }
              >
                {it.driving && <span className="dep-chip-driving-mark" aria-hidden="true">▸</span>}
                {variant !== "row" && (
                  <span className="dep-table-context-chip-arrow" aria-hidden="true">{arrow}</span>
                )}
                {(isDone || isExternal) && (
                  <span className="dep-table-chip-badge" aria-hidden="true">{isDone ? "✓" : "↗"}</span>
                )}
                <span className="dep-table-context-chip-code">
                  {it.other.work_code || it.other.task_code || "—"}
                </span>
                {!compact && (
                  <span className="dep-table-context-chip-name">
                    {it.other.work_code
                      ? it.other.work_name || activityLabel(it.other)
                      : activityLabel(it.other)}
                  </span>
                )}
                {!compact && it.other.zone?.c && (
                  <span className="dep-chip-zone">· {it.other.zone.c}</span>
                )}
                <span className="dep-table-context-chip-rel">· {rel}</span>
              </span>
            </Tooltip>
          );
        }
        const inLoop = cycleEdges?.has(`${it.predId}>${it.succId}`) ?? false;
        return (
          <span key={it.key} className={inLoop ? "dep-chip-loop" : undefined}>
          <DepChip
            workCode={it.other.work_code || it.other.task_code}
            workName={
              it.other.work_code
                ? it.other.work_name || activityLabel(it.other)
                : activityLabel(it.other)
            }
            fullName={it.other.name}
            taskCode={it.other.task_code}
            zoneLabel={it.other.zone?.c || showZone}
            color={it.other.color}
            predType={predTypeFromShort(it.type)}
            lagHours={it.lagDays * 8}
            driving={it.driving}
            compact={compact}
            variant={variant}
            candidates={candidates}
            currentId={side === "pred" ? it.predId : it.succId}
            onChangeRel={(type: PredType, days: number) =>
              intents.onRetypeEdge(it.succId, it.predId, shortFromPredType(type), days)
            }
            onReplace={
              intents.onReplaceEdge
                ? (newId: string, type: PredType, days: number) =>
                    // Drop the old edge + add the new one (same direction) in ONE op.
                    side === "pred"
                      ? intents.onReplaceEdge!(
                          it.succId, it.predId, newId, it.succId,
                          shortFromPredType(type), days,
                        )
                      : intents.onReplaceEdge!(
                          it.succId, it.predId, it.predId, newId,
                          shortFromPredType(type), days,
                        )
                : undefined
            }
            onDelete={() => intents.onRemoveEdge(it.succId, it.predId)}
          />
          </span>
        );
      })}
      {ghosts?.map((name) => (
        <Tooltip key={`ghost-${name}`} content={`${name} — deleted in this draft`}>
          <span className="dep-table-ghost-chip">{name}</span>
        </Tooltip>
      ))}
      {!readOnly && (
        <div className="dep-table-add-wrap">
          <button
            ref={addBtnRef}
            type="button"
            className="dep-table-add-btn"
            title={addTitle}
            onClick={() => setAdding((a) => !a)}
          >
            {addLabel}
          </button>
          {adding && (
            <DepChipTypeahead
              candidates={candidates}
              fetchExternals={allowExternalEdits ? fetchExternals : undefined}
              anchorRef={addBtnRef}
              onPick={onPick}
              onPickExternal={allowExternalEdits ? onPickExternal : undefined}
              onClose={handleClose}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ChipCell;
